import { useCallback, useRef } from 'react';
import { useChatContext } from '../store/ChatContext';
import {
  BotAgentStage,
  CartPreview,
  CheckoutReady,
  ProductDetailsPayload,
  ProductImagesPayload,
  SuggestedReply,
  UIMessage,
} from '../types/chat.types';

/**
 * Full bot:done payload — sideband fields are populated only when the matching
 * tool ran in this turn. See sky360-ms LLMStreamChunk for the wire contract.
 */
interface BotDonePayload {
  session_id?: string;
  metadata: any;
  searchResults?: { tool: string; results: any[]; items?: any[]; mode?: 'two_one_ways' };
  product_details?: ProductDetailsPayload;
  product_images?: ProductImagesPayload;
  cart_preview?: CartPreview;
  checkout_ready?: CheckoutReady;
  suggested_replies?: SuggestedReply[];
  current_stage?: BotAgentStage;
  /** The conversation language at the end of the turn (a primary subtag such as
   *  'he'). Absent when the BE does not know it or predates the field. */
  conversation_language?: string;
}

/**
 * `bot:error` wire payload. `code` / `retry_after_sec` ride along only on the
 * rejections that carry them (rate limit, policy reject) — see the gateway's
 * handleSendMessage.
 */
interface BotErrorPayload {
  session_id?: string;
  message: string;
  code?: string;
  retry_after_sec?: number;
}

/** `bot:warning` — one strike away from an auto-block. */
interface BotWarningPayload {
  session_id?: string;
  message: string;
  strikesRemaining?: number;
}

/** `bot:blocked` — the BE has disabled the session. No further turns will run. */
interface BotBlockedPayload {
  session_id?: string;
  message: string;
  reason?: string;
}

/** Mirrors BotRateLimitService.sessionMsgWindowSec — used when the BE tells us
 *  we're throttled but not for how long (older builds send no seconds). */
const SEND_COOLDOWN_FALLBACK_SEC = 60;
/** Clamp so a malformed/hostile value can't freeze the composer for hours. */
const SEND_COOLDOWN_MAX_SEC = 300;

/**
 * One of this hook's own transcript lines (error, warning, block, connection
 * drop). All of them are rendered the same way — a `system` bubble prefixed
 * with the warning glyph — so they stay visually distinct from the bot's prose.
 */
function systemMessage(text: string): UIMessage {
  return {
    id: crypto.randomUUID(),
    role: 'system',
    text: `⚠️ ${text}`,
    timestamp: new Date().toISOString(),
  };
}

function resolveRetrySec(raw?: number): number {
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return SEND_COOLDOWN_FALLBACK_SEC;
  return Math.min(Math.ceil(n), SEND_COOLDOWN_MAX_SEC);
}

export interface UseChatSocketOptions {
  namespace: string;           // e.g. '/bot-agent'
  onDone?: (metadata: any) => Promise<void>;
  /** Fired when the BE migrates this session into another channel (e.g. WhatsApp). */
  onHandedOff?: (sessionId: string, to: string) => void;
  /**
   * Fired on `bot:blocked` — the BE auto-disabled the session (security guard
   * or off-topic strike threshold). The host should flip the session into the
   * blocked state it already renders for a session that loads as blocked.
   */
  onBlocked?: (sessionId: string | undefined, reason?: string) => void;
  /**
   * Fired on `bot:error` with code `session_ended` — this conversation is over
   * (archived, or its token budget ran out) and every further message on it
   * will 403. Distinct from `onBlocked`: the user can recover from this one
   * themselves by starting a new chat, where a blocked session needs an
   * operator. Wire code owned by the BE — see BOT_ERROR_SESSION_ENDED in
   * bot-agent.service.ts.
   */
  onSessionEnded?: (sessionId: string | undefined) => void;
  /**
   * System line added to the transcript when the connection drops in the middle
   * of a turn. bot-core has no access to the host's translations, so the host
   * passes the localized copy in (see BotWidget: `t.bot.connectionError`).
   * Omitted → the turn is still flushed, just silently.
   */
  disconnectNotice?: string;
}

export function useChatSocket({
  namespace,
  onDone,
  onHandedOff,
  onBlocked,
  onSessionEnded,
  disconnectNotice,
}: UseChatSocketOptions) {
  const { state, dispatch } = useChatContext();
  const socketRef = useRef<any>(null);
  const lastToolRef = useRef<string | null>(null);

  // The session whose stream is currently on screen. ONE socket serves every
  // chat tab in the widget, and closing a tab / starting a new chat / switching
  // tabs does not disconnect it — so an in-flight turn for a session the user
  // has left keeps streaming. Without this guard its reply lands in whatever
  // chat is showing (the classic repro: send a message, close the tab before the
  // answer arrives, and the reply appears in the fresh empty chat).
  // Kept in a ref, not state, so the listeners bound once per connect() always
  // read the current value.
  const activeSessionRef = useRef<string | null>(null);

  /** True when an event belongs to the chat on screen. Events without a
   *  session_id are always accepted: pre-session validation errors are emitted
   *  before one exists, and an older backend doesn't stamp them at all. */
  const isForActiveSession = (sessionId?: string) =>
    !sessionId || sessionId === activeSessionRef.current;

  // The socket's event listeners are bound once per `connect()` and live for
  // the connection's lifetime. Keep the callbacks in refs so those listeners
  // always invoke the latest closures. This matters for the lazy-session flow:
  // the socket now connects on drawer open, before the first message creates
  // the session, so a listener bound while `sessionId` was still null would
  // otherwise keep firing with stale state and skip the post-turn cart/
  // checkout refresh (onDone early-returns when sessionId is falsy).
  const onDoneRef = useRef(onDone);
  const onHandedOffRef = useRef(onHandedOff);
  const onBlockedRef = useRef(onBlocked);
  const onSessionEndedRef = useRef(onSessionEnded);
  const disconnectNoticeRef = useRef(disconnectNotice);
  onDoneRef.current = onDone;
  onHandedOffRef.current = onHandedOff;
  onBlockedRef.current = onBlocked;
  onSessionEndedRef.current = onSessionEnded;
  disconnectNoticeRef.current = disconnectNotice;

  // Same reason as the callback refs: the listeners are bound once, so they
  // cannot read `state` directly. A turn counts as in flight while text is
  // streaming or a tool spinner is up.
  const turnInFlightRef = useRef(false);
  turnInFlightRef.current = state.isStreaming || !!state.activeToolLabel;

  // `connect()` awaits a dynamic import before it has a socket to store, so the
  // socketRef check alone let a fast open/close/open create two sockets and
  // orphan the first. These two are set synchronously, before the await:
  //   connectingRef  — a connect is already in progress, don't start another
  //   wantConnectedRef — the widget still wants a connection by the time the
  //                      import resolves; if it closed meanwhile, the socket we
  //                      just created is thrown away instead of leaking.
  const connectingRef = useRef(false);
  const wantConnectedRef = useRef(false);

  const connect = useCallback(async () => {
    wantConnectedRef.current = true;
    if (socketRef.current || connectingRef.current) return;
    connectingRef.current = true;
    dispatch({ type: 'SET_SOCKET_STATUS', status: 'connecting' });

    try {
      const { io } = await import('socket.io-client');
      const baseUrl = process.env.REACT_APP_MS_BASE_URL ?? '';
      const token = localStorage.getItem('agent-admin-token');

      const socket = io(`${baseUrl}${namespace}`, {
        auth: token ? { token } : undefined,
        transports: ['websocket'],
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
      });

      // The widget closed while the import was in flight — drop the socket we
      // just opened rather than leaving it connected with nobody listening.
      if (!wantConnectedRef.current) {
        socket.disconnect();
        dispatch({ type: 'SET_SOCKET_STATUS', status: 'disconnected' });
        return;
      }

      socket.on('connect', () => dispatch({ type: 'SET_SOCKET_STATUS', status: 'connected' }));
      socket.on('disconnect', (reason?: string) => {
        dispatch({ type: 'SET_SOCKET_STATUS', status: 'disconnected' });
        // The server aborts every in-flight turn when the socket drops
        // (BotAgentGateway.handleDisconnect), so no bot:done / bot:error is
        // ever coming: whatever we flush here is all the user will get. Without
        // this the streaming flags stay latched and ChatInput stays disabled
        // forever — reopening the widget doesn't help, the state is in memory.
        if (!turnInFlightRef.current) return;
        // FLUSH_MESSAGE clears isStreaming / streamingText / activeToolLabel and
        // only creates a bubble when there is text, so a drop before the first
        // chunk leaves no empty bubble behind.
        dispatch({ type: 'FLUSH_MESSAGE' });
        turnInFlightRef.current = false;
        // Don't leak this turn's tool into the next one's onDone.
        lastToolRef.current = null;
        // 'io client disconnect' is our own disconnect() (widget closed, tab
        // switched) — the turn still has to be flushed, but announcing a
        // connection problem the user didn't have would be a lie.
        if (reason === 'io client disconnect') return;
        if (disconnectNoticeRef.current) {
          dispatch({
            type: 'ADD_MESSAGE',
            message: systemMessage(disconnectNoticeRef.current),
          });
        }
      });
      socket.on('connect_error', () => dispatch({ type: 'SET_SOCKET_STATUS', status: 'error' }));

      socket.on('bot:chunk', ({ session_id, text }: { session_id?: string; text: string }) => {
        if (!isForActiveSession(session_id)) return;
        dispatch({ type: 'APPEND_CHUNK', text });
      });

      socket.on('bot:searching', ({ session_id, tool, label }: { session_id?: string; tool: string; label: string }) => {
        if (!isForActiveSession(session_id)) return;
        lastToolRef.current = tool;
        // A tool call ends whatever the model was saying and starts a new
        // segment, so close the paragraph before the next chunks arrive.
        dispatch({ type: 'MARK_SEGMENT_BREAK' });
        dispatch({ type: 'SET_TOOL_LABEL', label });
      });

      socket.on('bot:done', async (payload: BotDonePayload) => {
        if (!isForActiveSession(payload.session_id)) return;
        dispatch({
          type: 'FLUSH_MESSAGE',
          sideband: {
            searchResults: payload.searchResults,
            productDetails: payload.product_details,
            productImages: payload.product_images,
            cartPreview: payload.cart_preview,
            checkoutReady: payload.checkout_ready,
            suggestedReplies: payload.suggested_replies,
            currentStage: payload.current_stage,
          },
        });
        if (onDoneRef.current) {
          await onDoneRef.current({
            ...payload.metadata,
            lastTool: lastToolRef.current,
            checkoutReady: payload.checkout_ready,
            conversationLanguage: payload.conversation_language,
          });
        }
        lastToolRef.current = null;
      });

      socket.on(
        'session.handed_off',
        ({ session_id, to }: { session_id: string; to: string }) => {
          if (onHandedOffRef.current) onHandedOffRef.current(session_id, to);
        },
      );

      // One strike from an auto-block. Flush first so the bot's own reply keeps
      // its place in the transcript and the warning reads as what followed it.
      socket.on('bot:warning', ({ session_id, message }: BotWarningPayload) => {
        if (!isForActiveSession(session_id)) return;
        dispatch({ type: 'FLUSH_MESSAGE' });
        dispatch({ type: 'ADD_MESSAGE', message: systemMessage(message) });
      });

      // The session is disabled server-side. The host owns the blocked UI (the
      // banner + disabled composer it already renders for a session that loads
      // as blocked), so hand it over rather than duplicating that state here.
      socket.on('bot:blocked', ({ session_id, message, reason }: BotBlockedPayload) => {
        if (!isForActiveSession(session_id)) return;
        dispatch({ type: 'FLUSH_MESSAGE' });
        dispatch({ type: 'ADD_MESSAGE', message: systemMessage(message) });
        if (onBlockedRef.current) onBlockedRef.current(session_id, reason);
      });

      socket.on('bot:error', async ({ session_id, message, code, retry_after_sec }: BotErrorPayload) => {
        if (!isForActiveSession(session_id)) return;
        dispatch({ type: 'FLUSH_MESSAGE' });
        dispatch({ type: 'ADD_MESSAGE', message: systemMessage(message) });

        // Per-session message quota — hand the composer the same cooldown the
        // BE is enforcing instead of letting the user retry into another reject.
        if (code === 'rate_limited') {
          dispatch({
            type: 'SET_SEND_COOLDOWN',
            until: Date.now() + resolveRetrySec(retry_after_sec) * 1000,
          });
        }

        // The conversation is over — hand it to the host so it can close the
        // composer and offer a new chat. Without this the message above is the
        // whole experience: an error line, a live composer, and every retry
        // rejected the same way.
        if (code === 'session_ended') {
          onSessionEndedRef.current?.(session_id);
        }

        // A turn can fail AFTER its tools ran, so the cart may already have
        // changed server-side. onDone is the only thing that re-reads it; skip
        // it and the user re-asks for something they already have. Read + clear
        // the tool before awaiting so an abandoned turn can't leak it forward.
        const lastTool = lastToolRef.current;
        lastToolRef.current = null;
        // No metadata / checkout_ready exists on this path — handleBotTurnComplete
        // only ever reads lastTool and checkoutReady, and treats both as optional.
        if (onDoneRef.current) await onDoneRef.current({ lastTool });
      });

      socketRef.current = socket;
    } catch (err) {
      console.error('[ChatSocket] connect error', err);
      dispatch({ type: 'SET_SOCKET_STATUS', status: 'error' });
    } finally {
      connectingRef.current = false;
    }
  }, [dispatch, namespace]);

  const disconnect = useCallback(() => {
    wantConnectedRef.current = false;
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    // Deliberately NOT clearing activeSessionRef: the binding tracks which chat
    // is on screen, not the socket's health. Closing and reopening the widget
    // leaves the same session displayed, and the parent's bindSession effect
    // won't re-fire because sessionId never changed.
    dispatch({ type: 'SET_SOCKET_STATUS', status: 'disconnected' });
  }, [dispatch]);

  /**
   * Declare which session's stream the chat is showing. Call it whenever the
   * live session changes — tab switch, "new chat", closing the last tab (null).
   * Everything stamped with a different session is ignored from here on.
   */
  const bindSession = useCallback((sessionId: string | null) => {
    activeSessionRef.current = sessionId;
  }, []);

  /**
   * Send a turn. Binds the session as part of sending rather than relying on
   * the caller's bindSession effect: on the first message the session was
   * created moments ago and React has not re-rendered yet, so the ref would
   * still be null when the opening chunks arrive and the whole reply would be
   * filtered out.
   */
  const sendMessage = useCallback(
    (payload: { session_id: string } & Record<string, any>) => {
      if (!socketRef.current) return;
      activeSessionRef.current = payload.session_id;
      socketRef.current.emit('send_message', payload);
    },
    [],
  );

  /**
   * Ask the backend to abort a turn the user can no longer see. The socket stays
   * up (other tabs share it), so filtering alone would leave the LLM generating
   * — and billing — output for an abandoned chat.
   */
  const cancelTurn = useCallback((sessionId: string | null | undefined) => {
    if (!sessionId || !socketRef.current) return;
    socketRef.current.emit('cancel_turn', { session_id: sessionId });
  }, []);

  return { connect, disconnect, bindSession, sendMessage, cancelTurn };
}
